import styled from '@emotion/styled'
import { Black, Primary500, Warning500 } from '@mehilainen/mds-customer/colors'
import { ArrowRight, CircleInfo } from '@mehilainen/mds-customer/icons'
import { Button } from '@mui/material'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { AppointmentSearchResult } from '../../../__generated__/api'
import { scale } from '../../utils/scale'
import { formatDateAndTime } from '../../utils/text'
import Avatar from '../Avatar/Avatar'
import { CenteredRowFlex, ColumnFlex, RowFlex } from '../Layout/Layout'
import { Text } from '../Typography/Typography'

import { BaseTile } from './styles'

const StyledBaseTile = styled(BaseTile)`
  padding: ${scale(1.5)} ${scale(2)};
  &:focus-within {
    border-color: ${Primary500};
  }
`

const RowContainer = styled(RowFlex)<{ variant: VariantOptions }>`
  width: 100%;
  flex: 1;
  align-items: flex-start;
  gap: ${scale(2)};
`

const ColumnContainer = styled(ColumnFlex)`
  flex: 1;
`

const NextAppointmentDateContainer = styled(CenteredRowFlex)`
  margin-top: ${scale(1)};

  ${Text} {
    margin-right: ${scale(0.5)};
  }

  svg {
    width: 13px;
    height: 13px;
    color: ${Black};
  }
`

const TextContainer = styled(ColumnFlex)`
  flex: 1;
  gap: ${scale(0.5)};

  ${Text} {
    word-break: break-word;
    hyphens: auto;
  }
`

const TextWithIconContainer = styled(Text)<{ $align: 'center' | 'flex-start' }>`
  display: flex;
  align-items: ${(props) => props.$align};
  gap: ${scale(1)};
`

const StyledButton = styled(Button)`
  align-self: flex-start;
  margin-top: ${scale(1)};
`

type VariantOptions = 'S' | 'M' | 'L'

interface Props {
  appointment: AppointmentSearchResult
  variant: VariantOptions
  onClick?(): void
  onSpecialistClick?(): void
  autoFocus?: boolean
}

const SpecialistTile: React.FC<React.PropsWithChildren<Props>> = ({
  appointment,
  variant,
  onClick,
  onSpecialistClick,
  autoFocus,
}) => {
  const { t } = useTranslation()
  const defaultTextSize = variant === 'S' ? 300 : variant === 'M' ? 300 : 400
  const onClickHandler = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      onSpecialistClick?.()
    },
    [onSpecialistClick]
  )

  const onKeyHandler = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.stopPropagation()
        onSpecialistClick?.()
      }
    },
    [onSpecialistClick]
  )

  const buttonRef = useRef<HTMLLIElement>(null)

  useEffect(() => {
    if (autoFocus) {
      buttonRef.current?.focus()
    }
  }, [autoFocus])

  return (
    <StyledBaseTile
      onClick={onClick}
      onKeyPress={onClick}
      role="button"
      tabIndex={0}
      ref={buttonRef}
    >
      <RowContainer variant={variant}>
        <Avatar
          onClick={onSpecialistClick ? onClickHandler : undefined}
          onKeyPress={onSpecialistClick ? onKeyHandler : undefined}
          width={variant === 'S' ? '50px' : '64px'}
          height={variant === 'S' ? '70px' : '84px'}
          image={appointment.image}
          rating={appointment.specialistRating}
          borderRadius="10px"
          ratingFontSize="11px"
          ratingPadding="4px"
          tabIndex={0}
        />
        <ColumnContainer>
          <TextContainer>
            <TextWithIconContainer
              $size={defaultTextSize}
              $height="Medium"
              $weight="Bold"
              $color={onSpecialistClick ? `${Primary500}` : `${Black}`}
              $align="center"
              onClick={onSpecialistClick ? onClickHandler : undefined}
              as="h3"
            >
              <span data-cy="specialistName">{appointment.specialistName}</span>
            </TextWithIconContainer>
            <Text $size={defaultTextSize} $height="Medium" $weight="Regular">
              {appointment.specialistTitle}
            </Text>
          </TextContainer>
          {appointment.specialistAllowsNewKelaClients === false && (
            <TextWithIconContainer $size={200} $color={Warning500} $align="flex-start">
              <CircleInfo />
              <span>{t('component.appointmentList.disallowsKelaClients')}</span>
            </TextWithIconContainer>
          )}
          <NextAppointmentDateContainer>
            <Text $size={defaultTextSize} $weight="Regular" $height="Small">
              {formatDateAndTime(dayjs(appointment.time), t)}
            </Text>
            <ArrowRight />
          </NextAppointmentDateContainer>
          <StyledButton
            size="small"
            data-analytics-path="acc-person-free-rec-time"
            aria-label={`${t('component.practitionerList.additionalInformationButtonText')}, ${
              appointment.specialistName
            }`}
          >
            {t('component.practitionerList.additionalInformationButtonText')}
          </StyledButton>
        </ColumnContainer>
      </RowContainer>
    </StyledBaseTile>
  )
}

export default SpecialistTile
